import type { FilterLabels, GlobalFilters } from "./portfolio.types"

export const FILTER_DIMENSIONS = [
  "project",
  "pm",
  "client",
  "project_type",
  "project_status",
  "state"
] as const

export type FilterDimension = (typeof FILTER_DIMENSIONS)[number]

export const createEmptyFilters = (): GlobalFilters => ({
  project: [],
  pm: [],
  client: [],
  project_type: [],
  project_status: [],
  state: []
})

export const EMPTY_GLOBAL_FILTERS: GlobalFilters = createEmptyFilters()

// Contract (BE mirrors this): each dimension serializes as a comma-separated id
// list under a query param named exactly after the dimension; an empty dimension
// is omitted, so an absent param means that dimension is unfiltered.
export const buildFilterParams = (
  filters: GlobalFilters | undefined
): Record<string, string> => {
  const params: Record<string, string> = {}
  if (!filters) return params
  for (const dimension of FILTER_DIMENSIONS) {
    const values = filters[dimension]
    if (values && values.length > 0) params[dimension] = values.join(",")
  }
  return params
}

export const parseGlobalFilters = (config: unknown): GlobalFilters => {
  const result = createEmptyFilters()
  if (!config || typeof config !== "object" || Array.isArray(config))
    return result

  const raw = (config as { globalFilters?: unknown }).globalFilters
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return result

  for (const dimension of FILTER_DIMENSIONS) {
    const values = (raw as Record<string, unknown>)[dimension]
    if (Array.isArray(values)) {
      result[dimension] = values
        .filter((v) => typeof v === "string" || typeof v === "number")
        .map((v) => String(v))
    }
  }
  return result
}

export const createEmptyFilterLabels = (): FilterLabels => ({})

export const parseFilterLabels = (config: unknown): FilterLabels => {
  const result: FilterLabels = {}
  if (!config || typeof config !== "object" || Array.isArray(config))
    return result

  const raw = (config as { filterLabels?: unknown }).filterLabels
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return result

  for (const dimension of FILTER_DIMENSIONS) {
    const entry = (raw as Record<string, unknown>)[dimension]
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue
    const labels: Record<string, string> = {}
    for (const [value, label] of Object.entries(
      entry as Record<string, unknown>
    )) {
      if (typeof label === "string") labels[value] = label
    }
    if (Object.keys(labels).length > 0) result[dimension] = labels
  }
  return result
}

const activeViewFromConfig = (
  config: unknown
): Record<string, unknown> | null => {
  if (!config || typeof config !== "object" || Array.isArray(config))
    return null
  const raw = config as Record<string, unknown>
  if (!Array.isArray(raw.views)) return null

  const views = raw.views.filter(
    (view): view is Record<string, unknown> =>
      Boolean(view) && typeof view === "object" && !Array.isArray(view)
  )
  return (
    views.find((view) => view.id === raw.activeViewId) ??
    views.find((view) => view.id === raw.defaultViewId) ??
    views[0] ??
    null
  )
}

export const parseActiveViewGlobalFilters = (
  config: unknown
): GlobalFilters => {
  const view = activeViewFromConfig(config)
  if (view && Object.hasOwn(view, "globalFilters"))
    return parseGlobalFilters(view)
  return parseGlobalFilters(config)
}

export const parseActiveViewFilterLabels = (config: unknown): FilterLabels => {
  const view = activeViewFromConfig(config)
  if (view && Object.hasOwn(view, "filterLabels"))
    return parseFilterLabels(view)
  return parseFilterLabels(config)
}

export const countActiveFilters = (
  filters: GlobalFilters | undefined
): number =>
  filters
    ? FILTER_DIMENSIONS.filter(
        (dimension) => (filters[dimension]?.length ?? 0) > 0
      ).length
    : 0

export const hasActiveFilters = (filters: GlobalFilters | undefined): boolean =>
  countActiveFilters(filters) > 0
